import { Capacitor } from "@capacitor/core";
import { openDB } from "idb";
import type { Repository } from "../data/Repository";
import type { CrmInput } from "../types";
import { processQueueItems } from "./outboxPolicy";
import { SecureStorage } from "./secureStoragePlugin";

export { isRetryableQueueError, nextRetryAt } from "./outboxPolicy";

export type QueueItemState = "pending" | "failed";

export interface QueuedInput {
  id: string;
  userId: string;
  payload: CrmInput;
  queuedAt: string;
  attemptCount: number;
  nextAttemptAt: string | null;
  lastError: string | null;
  state: QueueItemState;
}

export interface OutboxStatus {
  items: QueuedInput[];
  pending: number;
  failed: number;
}

interface QueueEnvelope extends Omit<QueuedInput, "id"> {
  version: 2;
}

interface LegacyWebItem {
  id: string;
  payload: CrmInput;
  queuedAt: string;
}

const OUTBOX_CHANGED_EVENT = "justkasz:outbox-changed";

const webDb = openDB("justkasz-crm-v1", 1, {
  upgrade(database) {
    if (!database.objectStoreNames.contains("outbox")) {
      database.createObjectStore("outbox", { keyPath: "id" });
    }
  },
});

function notifyOutboxChanged(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(OUTBOX_CHANGED_EVENT));
}

function isQueueEnvelope(value: unknown): value is QueueEnvelope {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<QueueEnvelope>;
  return candidate.version === 2 && typeof candidate.userId === "string" && !!candidate.payload;
}

function envelope(item: QueuedInput): QueueEnvelope {
  return {
    version: 2,
    userId: item.userId,
    payload: item.payload,
    queuedAt: item.queuedAt,
    attemptCount: item.attemptCount,
    nextAttemptAt: item.nextAttemptAt,
    lastError: item.lastError,
    state: item.state,
  };
}

async function storeItem(item: QueuedInput): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    await SecureStorage.enqueue({ id: item.id, payload: JSON.stringify(envelope(item)) });
  } else {
    const database = await webDb;
    await database.put("outbox", { id: item.id, ...envelope(item) });
  }
  notifyOutboxChanged();
}

export async function queueInput(payload: CrmInput, userId: string): Promise<void> {
  await storeItem({
    id: payload.client_event_id,
    userId,
    payload,
    queuedAt: new Date().toISOString(),
    attemptCount: 0,
    nextAttemptAt: null,
    lastError: null,
    state: "pending",
  });
}

async function readAllItems(userId: string): Promise<QueuedInput[]> {
  const records: Array<{ id: string; stored: unknown; queuedAt: string }> = [];

  if (Capacitor.isNativePlatform()) {
    const { items } = await SecureStorage.list();
    for (const item of items) {
      records.push({ id: item.id, stored: JSON.parse(item.payload), queuedAt: item.queuedAt });
    }
  } else {
    const database = await webDb;
    for (const item of (await database.getAll("outbox")) as Array<
      LegacyWebItem | ({ id: string } & QueueEnvelope)
    >) {
      records.push({ id: item.id, stored: item, queuedAt: item.queuedAt });
    }
  }

  const result: QueuedInput[] = [];
  for (const record of records) {
    const stored = record.stored as ({ id?: string } & Partial<QueueEnvelope>) | CrmInput;
    const candidate = isQueueEnvelope(stored) ? stored : null;

    if (candidate) {
      if (candidate.userId === userId) result.push({ id: record.id, ...candidate });
      continue;
    }

    // Versions before 0.3.0 had no owner. Bind each legacy item once to the
    // authenticated user who first opens the upgraded app, then persist v2.
    const legacyPayload =
      "payload" in stored ? (stored as LegacyWebItem).payload : (stored as CrmInput);
    const claimed: QueuedInput = {
      id: record.id,
      userId,
      payload: legacyPayload,
      queuedAt: record.queuedAt,
      attemptCount: 0,
      nextAttemptAt: null,
      lastError: null,
      state: "pending",
    };
    await storeItem(claimed);
    result.push(claimed);
  }

  return result.sort((first, second) => first.queuedAt.localeCompare(second.queuedAt));
}

export async function listQueuedInputs(userId: string): Promise<QueuedInput[]> {
  return readAllItems(userId);
}

export async function getOutboxStatus(userId: string): Promise<OutboxStatus> {
  const items = await readAllItems(userId);
  return {
    items,
    pending: items.filter((item) => item.state === "pending").length,
    failed: items.filter((item) => item.state === "failed").length,
  };
}

export async function removeQueuedInput(id: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    await SecureStorage.remove({ id });
  } else {
    const database = await webDb;
    await database.delete("outbox", id);
  }
  notifyOutboxChanged();
}

export function subscribeToOutbox(listener: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  window.addEventListener(OUTBOX_CHANGED_EVENT, listener);
  return () => window.removeEventListener(OUTBOX_CHANGED_EVENT, listener);
}

const runningFlushes = new Map<string, Promise<OutboxStatus>>();

export function flushOutbox(
  repository: Repository,
  userId: string,
  options: { force?: boolean; now?: number } = {},
): Promise<OutboxStatus> {
  const current = runningFlushes.get(userId);
  if (current) return current;

  const run = (async () => {
    const items = await readAllItems(userId);
    await processQueueItems(
      items,
      {
        save: async (item) => repository.saveInput(item.payload).then(() => undefined),
        remove: async (item) => removeQueuedInput(item.id),
        store: storeItem,
      },
      options,
    );
    return getOutboxStatus(userId);
  })().finally(() => runningFlushes.delete(userId));

  runningFlushes.set(userId, run);
  return run;
}
